import React, { useEffect, useState, useRef } from 'react';
import { Drawer, Form, Input, Select, Button, message } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTemplatePatterns } from '../TemplatesAccommodations/actions';
import { getTemplatePatterns } from '../TemplatesAccommodations/selectors';
import { saveRamp } from './ramps-actions';

interface Props {
  open: boolean;
  onClose: () => void;
  modalData?: any;
  isClone?: boolean;
}

const rampTypes = ['Integration', 'New Hire', 'New Grad', 'De Novo'];

const AddRampDrawer: React.FC<Props> = ({ open, onClose, modalData, isClone = false }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const patternsFromStore = useSelector(getTemplatePatterns) || [];
  const [saving, setSaving] = useState(false);
  const [formValues, setFormValues] = useState<any>({});
  const prevFormValuesRef = useRef<any>({});

  useEffect(() => {
    if (open) {
      dispatch(fetchTemplatePatterns());
      if (modalData) {
        // Normalize values coming from table (which may have '-' for empty) back to null
        const normalize = (v: any) => (v === '-' ? null : v === undefined ? null : v);
        const initial = {
          rampName: isClone ? '' : modalData.rampName || '',
          type: normalize(modalData.rampType || ''),
          week1: normalize(modalData.week1 || null),
          week2: normalize(modalData.week2 || null),
          week3: normalize(modalData.week3 || null),
          week4: normalize(modalData.week4 || null),
          week5: normalize(modalData.week5 || null),
          week6: normalize(modalData.week6 || null),
          week7: normalize(modalData.week7 || null),
          week8: normalize(modalData.week8 || null),
        };
        form.setFieldsValue(initial);
        prevFormValuesRef.current = initial;
        setFormValues(initial);
      } else {
        form.resetFields();
        prevFormValuesRef.current = {};
        setFormValues({});
      }
    }
  }, [open, modalData]);

  const handleSave = (values: any) => {
    if (!values.rampName || !values.type || !values.week1) {
      message.error('Please fill required fields');
      return;
    }

    const payload = {
      ...(!isClone && modalData && modalData.key && { rampId: Number(modalData.key) }),
      name: values.rampName,
      type: values.type,
      week1Pattern: values.week1 || null,
      week2Pattern: values.week2 || null,
      week3Pattern: values.week3 || null,
      week4Pattern: values.week4 || null,
      week5Pattern: values.week5 || null,
      week6Pattern: values.week6 || null,
      week7Pattern: values.week7 || null,
      week8Pattern: values.week8 || null,
    };

    setSaving(true);
    dispatch(saveRamp(payload));
    // rely on saga to refresh and handle notifications; close drawer here
    setSaving(false);
    onClose();
  };

  const patterns = Array.isArray(patternsFromStore)
    ? patternsFromStore.map((p) => (typeof p === 'string' ? p.trim() : p))
    : [];

  const drawerTitle = isClone ? 'Add New Ramp Definition' : modalData ? 'Edit Ramp Definition' : 'Add Ramp Definition';

  return (
    <Drawer title={drawerTitle} placement='right' onClose={onClose} open={open} width={420}>
      <Form
        form={form}
        layout='vertical'
        onFinish={handleSave}
        onValuesChange={(changed, all) => {
          const previous = prevFormValuesRef.current || {};
          setFormValues(all);
          prevFormValuesRef.current = all;
        }}
      >
        <Form.Item label={'Ramp Name'} name='rampName' rules={[{ required: true, message: 'Ramp Name is required' }]}>
          <Input placeholder='Enter Ramp Name' />
        </Form.Item>

        <Form.Item label='Ramp Type' name='type' rules={[{ required: true, message: 'Ramp Type is required' }]}>
          <Select placeholder='Select Ramp Type'>
            {rampTypes.map((t) => (
              <Select.Option key={t} value={t}>
                {t}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => {
          const prevKey = `week${n - 1}`;
          const getField = (key: string) => {
            const v = form.getFieldValue ? form.getFieldValue(key) : undefined;
            return v === undefined ? formValues?.[key] : v;
          };
          const disabled = n > 1 && !getField(prevKey);
          const laterSelected = Array.from({ length: 8 - n }, (_, i) => i + n + 1).some((i) => !!getField(`week${i}`));
          return (
            <Form.Item
              key={`week${n}`}
              label={`Week ${n} Pattern`}
              name={`week${n}`}
              rules={n === 1 ? [{ required: true, message: 'Week 1 pattern is required' }] : undefined}
            >
              <Select
                showSearch
                allowClear
                clearIcon={
                  laterSelected ? (
                    <CloseCircleOutlined
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        message.error('Please remove next week pattern first, then try again');
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                  ) : (
                    <CloseCircleOutlined />
                  )
                }
                placeholder={`Select Week ${n} Pattern`}
                disabled={disabled}
                filterOption={(input, option) => {
                  const value = option?.children || option?.value || '';
                  return String(value).toLowerCase().includes(input.toLowerCase());
                }}
                onChange={(value) => {
                  form.setFieldsValue({ [`week${n}`]: value === undefined ? null : value });
                  setFormValues((fv: any) => ({ ...(fv || {}), [`week${n}`]: value === undefined ? null : value }));
                  prevFormValuesRef.current = {
                    ...(prevFormValuesRef.current || {}),
                    [`week${n}`]: value === undefined ? null : value,
                  };
                }}
              >
                {patterns.map((p) => (
                  <Select.Option key={p} value={p}>
                    {p}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          );
        })}

        {/* Range 0 Pattern removed as it's not needed anymore */}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type='primary' htmlType='submit' loading={saving}>
            {isClone ? 'Create New' : modalData ? 'Save' : 'Save'}
          </Button>
        </div>
      </Form>
    </Drawer>
  );
};

export default AddRampDrawer;
